import { useState, useCallback } from 'react'
import { rewriteApi, RewriteContent, RewriteContentVersion } from '@/services/api'

interface UseContentEditorOptions {
  contents: RewriteContent[]
  updateContent: (id: string, data: { title?: string; sections?: Array<{ type: string; title: string; body: string }>; status?: string }) => Promise<void>
  rewriteSection: (contentId: string, sectionIndex: number, instruction?: string, guidelineIds?: string[], customGuidelines?: string[], model?: string) => Promise<string | null>
  restoreVersion: (contentId: string, versionId: string) => Promise<boolean>
  getGuidelineContext?: () => { guidelineIds?: string[]; customGuidelines?: string[]; model?: string }
}

export function useContentEditor({ contents, updateContent, rewriteSection, restoreVersion, getGuidelineContext }: UseContentEditorOptions) {
  const [editingSection, setEditingSection] = useState<{ contentId: string; index: number } | null>(null)
  const [editText, setEditText] = useState('')
  const [rewritingSection, setRewritingSection] = useState<{ contentId: string; index: number } | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState<string | null>(null)
  const [versionMap, setVersionMap] = useState<Record<string, RewriteContentVersion[]>>({})
  const [restoringVersion, setRestoringVersion] = useState(false)
  const [diffMode, setDiffMode] = useState(false)

  const handleSaveSection = useCallback(async (contentId: string, sectionIndex: number) => {
    const content = contents.find(c => c.id === contentId)
    if (!content) return
    const sections = typeof content.sections === 'string' ? JSON.parse(content.sections) : content.sections
    const updated = [...sections]
    updated[sectionIndex] = { ...updated[sectionIndex], body: editText }
    await updateContent(contentId, { sections: updated })
    setEditingSection(null)
  }, [editText, contents, updateContent])

  const handleRewrite = useCallback(async (contentId: string, sectionIndex: number) => {
    setRewritingSection({ contentId, index: sectionIndex })
    const ctx = getGuidelineContext?.()
    const result = await rewriteSection(contentId, sectionIndex, undefined, ctx?.guidelineIds, ctx?.customGuidelines, ctx?.model)
    if (result) {
      const content = contents.find(c => c.id === contentId)
      if (content) {
        const sections = typeof content.sections === 'string' ? JSON.parse(content.sections) : content.sections
        const updated = [...sections]
        updated[sectionIndex] = { ...updated[sectionIndex], body: result }
        await updateContent(contentId, { sections: updated })
      }
    }
    setRewritingSection(null)
  }, [contents, rewriteSection, updateContent, getGuidelineContext])

  const handleCopy = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }, [])

  const handleCopyAll = useCallback((contentId: string) => {
    const content = contents.find(c => c.id === contentId)
    if (!content) return
    const sections = typeof content.sections === 'string' ? JSON.parse(content.sections) : content.sections
    const fullText = sections.map((s: { title: string; body: string }) => `## ${s.title}\n${s.body}`).join('\n\n')
    handleCopy(fullText, contentId)
  }, [contents, handleCopy])

  const handleDownload = useCallback((contentId: string) => {
    const content = contents.find(c => c.id === contentId)
    if (!content) return
    const sections = typeof content.sections === 'string' ? JSON.parse(content.sections) : content.sections
    const fullText = sections.map((s: { title: string; body: string }) => `## ${s.title}\n${s.body}`).join('\n\n')
    const blob = new Blob([fullText], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${content.title || 'content'}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }, [contents])

  const handleStatusChange = useCallback(async (contentId: string, newStatus: string) => {
    await updateContent(contentId, { status: newStatus })
  }, [updateContent])

  const handleToggleHistory = useCallback(async (contentId: string) => {
    if (showHistory === contentId) { setShowHistory(null); return }
    setShowHistory(contentId)
    if (!versionMap[contentId]) {
      const res = await rewriteApi.contents.get(contentId)
      if (res.success && res.data) {
        const versions = (res.data as unknown as { versions?: RewriteContentVersion[] }).versions || []
        setVersionMap(prev => ({ ...prev, [contentId]: versions }))
      }
    }
  }, [showHistory, versionMap])

  const handleRestoreVersion = useCallback(async (contentId: string, versionId: string) => {
    setRestoringVersion(true)
    const success = await restoreVersion(contentId, versionId)
    if (success) {
      const res = await rewriteApi.contents.get(contentId)
      if (res.success && res.data) {
        const versions = (res.data as unknown as { versions?: RewriteContentVersion[] }).versions || []
        setVersionMap(prev => ({ ...prev, [contentId]: versions }))
      }
    }
    setRestoringVersion(false)
  }, [restoreVersion])

  const startEditing = useCallback((contentId: string, index: number, body: string) => {
    setEditingSection({ contentId, index })
    setEditText(body)
  }, [])

  const cancelEditing = useCallback(() => {
    setEditingSection(null)
  }, [])

  return {
    editingSection,
    editText,
    setEditText,
    rewritingSection,
    copiedId,
    showHistory,
    versionMap,
    restoringVersion,
    diffMode,
    setDiffMode,
    handleSaveSection,
    handleRewrite,
    handleCopy,
    handleCopyAll,
    handleDownload,
    handleStatusChange,
    handleToggleHistory,
    handleRestoreVersion,
    startEditing,
    cancelEditing,
  }
}
